import type { Card, DraftPayload, NewProjectForm, Project, ProjectBundle, Template, User, Workspace } from "./types";

export const API_BASE = (import.meta.env.VITE_API_BASE_URL || "http://localhost:8787").replace(/\/$/, "");

type AuthResponse = {
  token: string;
  user: User;
  workspace: Workspace;
};

export class ApiClient {
  constructor(private token: string | null) {}

  setToken(token: string | null): void {
    this.token = token;
  }

  async login(email: string, name?: string): Promise<AuthResponse> {
    return this.request<AuthResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, name })
    });
  }

  async me(): Promise<{ user: User; workspace: Workspace }> {
    return this.request("/api/auth/me");
  }

  async logout(): Promise<void> {
    await this.request("/api/auth/logout", { method: "POST" });
  }

  async fetchUrl(url: string): Promise<{ title: string; text: string; success: boolean }> {
    return this.request("/api/fetch-url", {
      method: "POST",
      body: JSON.stringify({ url })
    });
  }

  async createProject(form: NewProjectForm): Promise<{ project: Project }> {
    return this.request("/api/projects", {
      method: "POST",
      body: JSON.stringify({
        ...form,
        title: form.title || undefined,
        source_text: form.source_text || undefined,
        source_url: form.source_url || undefined,
        card_count: Number(form.card_count)
      })
    });
  }

  async listProjects(): Promise<{ projects: Project[] }> {
    return this.request("/api/projects");
  }

  async getProject(projectId: string): Promise<ProjectBundle> {
    return this.request(`/api/projects/${projectId}`);
  }

  async generateCopy(projectId: string): Promise<{ draft: DraftPayload; cards: Card[] }> {
    return this.request(`/api/projects/${projectId}/generate-copy`, { method: "POST" });
  }

  async patchProject(projectId: string, body: Partial<NewProjectForm & { fetched_content: string }>): Promise<{ project: Project }> {
    return this.request(`/api/projects/${projectId}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    });
  }

  async patchDraft(draftId: string, body: Partial<DraftPayload>): Promise<{ draft: DraftPayload }> {
    return this.request(`/api/drafts/${draftId}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    });
  }

  async patchCard(cardId: string, body: Partial<Card>): Promise<{ card: Card }> {
    return this.request(`/api/cards/${cardId}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    });
  }

  async duplicateCard(cardId: string): Promise<{ cards: Card[] }> {
    return this.request(`/api/cards/${cardId}/duplicate`, { method: "POST" });
  }

  async deleteCard(cardId: string): Promise<{ cards: Card[] }> {
    return this.request(`/api/cards/${cardId}`, { method: "DELETE" });
  }

  async reorderCards(projectId: string, orderedIds: string[]): Promise<{ cards: Card[] }> {
    return this.request(`/api/projects/${projectId}/cards/reorder`, {
      method: "POST",
      body: JSON.stringify({ ordered_ids: orderedIds })
    });
  }

  async generateImages(projectId: string, cardIds?: string[]): Promise<{ task_ids: string[]; status: string }> {
    return this.request(`/api/projects/${projectId}/generate-images`, {
      method: "POST",
      body: JSON.stringify({ provider: "openai", model: "gpt-image-1", card_ids: cardIds })
    });
  }

  async retryCard(cardId: string): Promise<{ task_ids: string[]; status: string }> {
    return this.request(`/api/cards/${cardId}/generate-image`, {
      method: "POST",
      body: JSON.stringify({ provider: "openai", model: "gpt-image-1" })
    });
  }

  async generationStatus(projectId: string): Promise<ProjectBundle> {
    return this.request(`/api/projects/${projectId}/generation-status`);
  }

  async copyText(projectId: string): Promise<{ copy_text: string }> {
    return this.request(`/api/projects/${projectId}/copy-text`);
  }

  async imageZip(projectId: string): Promise<Blob> {
    return this.requestBlob(`/api/projects/${projectId}/export/images.zip`);
  }

  async listTemplates(): Promise<{ templates: Template[] }> {
    return this.request("/api/templates");
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        "content-type": "application/json",
        ...(this.token ? { authorization: `Bearer ${this.token}` } : {}),
        ...init.headers
      }
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(payload.error || response.statusText);
    }
    return response.json() as Promise<T>;
  }

  private async requestBlob(path: string): Promise<Blob> {
    const response = await fetch(`${API_BASE}${path}`, {
      headers: this.token ? { authorization: `Bearer ${this.token}` } : undefined
    });
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return response.blob();
  }
}

export function resolveImageUrl(imageUrl: string | null): string {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("data:")) return imageUrl;
  if (imageUrl.startsWith("/")) return `${API_BASE}${imageUrl}`;
  return imageUrl;
}
